import React, { useEffect, useRef, useState, memo } from "react";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapLocationDot,
  faCopy,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import { mapService } from "../../services/MapService";

const ADDRESS = "경기도 부천시 부일로 278, 강남빌딩 3-4층";
const MAP_TITLE = "처음사랑교회";

const AddressInfo = memo(({ address }: { address: string }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy address:", err);
    }
  };

  return (
    <ValueWrapper>
      <AddressValue>{address}</AddressValue>
      <CopyButton onClick={handleCopyAddress} $active={copySuccess}>
        <FontAwesomeIcon icon={faCopy} />
        {copySuccess ? " 복사되었습니다" : " 복사"}
      </CopyButton>
    </ValueWrapper>
  );
});

AddressInfo.displayName = "AddressInfo";

const LocationSection = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkKakaoMap = () => {
      if (window.kakao && window.kakao.maps && mapContainer.current) {
        try {
          mapService.initWithAddress(
            mapContainer.current,
            "경기도 부천시 부일로 278",
            MAP_TITLE,
          );
          setMapLoaded(true);
        } catch (error) {
          console.error("Map initialization failed:", error);
          setMapError(true);
        }
      } else {
        setTimeout(checkKakaoMap, 100);
      }
    };

    checkKakaoMap();
  }, []);

  const openExternalMap = (type: "kakao" | "naver") => {
    const urls = {
      kakao: `https://kko.to/5qCX7VxX1J`,
      naver: `https://naver.me/xTsKgPmi`,
    };
    window.open(urls[type], "_blank");
  };

  return (
    <Section
      className={`location-section ${isVisible ? "active" : ""}`}
      id="location"
      ref={sectionRef}
    >
      <Container className="container">
        <SectionHeader className="section-header">
          <SectionTitle className="section-title">
            <FontAwesomeIcon icon={faMapLocationDot} className="section-icon" />{" "}
            오시는 길
          </SectionTitle>
          <SectionSubtitle>
            처음사랑교회로 오시는 길을 안내해 드립니다
          </SectionSubtitle>
        </SectionHeader>

        <ContentWrapper className="content-wrapper">
          <MapWrapper>
            {mapError ? (
              <MapFallback>
                <FontAwesomeIcon
                  icon={faMapLocationDot}
                  className="fallback-icon"
                />
                <FallbackText>지도를 불러올 수 없습니다</FallbackText>
                <FallbackSubText>
                  아래 버튼을 사용하여 길을 찾으실 수 있습니다
                </FallbackSubText>
              </MapFallback>
            ) : (
              <KakaoMapBox ref={mapContainer}>
                {!mapLoaded && (
                  <MapLoading>
                    <div className="spinner"></div>
                    <LoadingText>지도를 불러오는 중...</LoadingText>
                  </MapLoading>
                )}
              </KakaoMapBox>
            )}
          </MapWrapper>

          <InfoCard>
            <InfoContent>
              <InfoGroup>
                <Label>주소</Label>
                <AddressInfo address={ADDRESS} />
              </InfoGroup>

              <InfoGroup>
                <Label>지도 바로가기</Label>
                <ButtonGroup>
                  <ExternalButton
                    onClick={() => openExternalMap("kakao")}
                    $color="#FEE500"
                    $textColor="#3C1E1E"
                  >
                    <span className="kakao-k">K</span>
                    카카오맵
                  </ExternalButton>
                  <ExternalButton
                    onClick={() => openExternalMap("naver")}
                    $color="#03C75A"
                    $textColor="#FFFFFF"
                  >
                    <span className="naver-n">N</span>
                    네이버 지도
                  </ExternalButton>
                </ButtonGroup>
              </InfoGroup>
            </InfoContent>
          </InfoCard>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const Section = styled.section`
  background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
  padding: 100px 0;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;

  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease-out;

  .active & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  color: #004094;
  margin-bottom: 8px;
  font-weight: 700;
  letter-spacing: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  .section-icon {
    color: #004094;
    font-size: 0.9em;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 60% 1fr; /* 비율을 고정하여 내부 콘텐츠 변화에 따른 컬럼 너비 변화 방지 */
  gap: 30px;

  opacity: 0;
  transform: translateY(20px);
  transition: all 1s ease-out 0.2s;

  .active & {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const MapWrapper = styled.div`
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 40, 0.08);
  border: 1px solid rgba(0, 64, 148, 0.05);
  height: 450px;
  position: relative;

  /* GPU 가속 유도 */
  transform: translateZ(0);

  @media (max-width: 768px) {
    height: 350px;
  }
`;

const KakaoMapBox = styled.div`
  width: 100%;
  height: 100%;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 40, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0; /* 그리드 내에서 내부 콘텐츠에 의해 너비가 변하는 것 방지 */

  /* 번쩍임 방지 최적화 */
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform, opacity;

  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;
`;

const InfoGroup = styled.div``;

const Label = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #007aff;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
`;

const AddressValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.5;
  word-break: keep-all;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CopyButton = styled.button<{ $active: boolean }>`
  background: ${(props) => (props.$active ? "#4caf50" : "#f0f4f9")};
  color: ${(props) => (props.$active ? "white" : "#004094")};
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 160px; /* 고정 너비 적용 */
  align-self: flex-start; /* 부모의 stretch 영향을 받지 않고 왼쪽 정렬 */

  &:hover {
    background: ${(props) => (props.$active ? "#43a047" : "#e1e9f5")};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ExternalButton = styled.button<{ $color: string; $textColor: string }>`
  flex: 1;
  background: ${(props) => props.$color};
  color: ${(props) => props.$textColor};
  border: none;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  &:hover {
    filter: brightness(0.95);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }

  .naver-n {
    background: #ffffff;
    color: #03c75a;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    font-weight: 900;
    font-size: 14px;
    font-family: "Paperlogy", sans-serif;
  }

  .kakao-k {
    background: #3c1e1e;
    color: #fee500;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-weight: 900;
    font-size: 14px;
    font-family: "Paperlogy", sans-serif;
  }
`;

const MapLoading = styled.div`
  position: absolute;
  inset: 0;
  background: #f8faff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0, 64, 148, 0.1);
    border-top-color: #004094;
    border-radius: 50%;
    animation: ${pulse} 1s infinite linear;
    margin-bottom: 15px;
  }
`;

const LoadingText = styled.div`
  font-size: 15px;
  color: #666;
  font-weight: 500;
`;

const MapFallback = styled.div`
  width: 100%;
  height: 100%;
  background: #f8faff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;

  .fallback-icon {
    font-size: 48px;
    color: #004094;
    opacity: 0.2;
    margin-bottom: 20px;
  }
`;

const FallbackText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const FallbackSubText = styled.div`
  font-size: 14px;
  color: #666;
  text-align: center;
`;

export default LocationSection;
